import {
  Component, DestroyRef,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IconComponent } from 'app/shared/icon/icon.component';
import { MIN_QUERY_LENGTH } from '../friends.const';
import { FriendsService } from '../service/friends.service';
import { ContactEvent, IContact, IContactResponse } from '../model/friends.model';
import { debounceTime, exhaustMap, filter, finalize, map, of, Subscription } from 'rxjs';
import { provideIcons } from '@ng-icons/core';
import { heroArrowLongLeft } from '@ng-icons/heroicons/outline';
import { ContactCardComponent } from "../contact-card/contact-card.component";
import { DialogService } from "app/shared/dialog/dialog.service";
import { InfoDialogComponent } from "app/shared/dialog/info-dialog/info-dialog.component";
import { ButtonAction } from "app/shared/dialog/dialog-data";
import { NotificationService } from "app/shared/notification.service";

@Component({
  selector: 'app-my-contacts-list',
  standalone: true,
  imports: [FormsModule, IconComponent, ReactiveFormsModule, ContactCardComponent],
  templateUrl: './my-contacts-list.component.html',
  styleUrls: ['./my-contacts-list.component.scss'],
  providers: [provideIcons({ heroArrowLongLeft })],
})
export class MyContactsListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tabUpdated!: number;
  @ViewChild('scrollContactsContainer', { static: false }) scrollContactsContainer!: ElementRef;

  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);
  private notification = inject(NotificationService);

  private subscriptions = new Subscription();
  private isContactsFetching = false;
  private allContactsLoaded = false;

  friendsService = inject(FriendsService);
  contactsList = signal<IContact[]>([]);
  contactsTotal = this.friendsService.contactsTotal;

  public searchContact = new FormControl('', [Validators.minLength(MIN_QUERY_LENGTH)]);

  ngOnInit() {
    this.getInitialContacts();

    this.subscriptions.add(
      this.searchContact.valueChanges
        .pipe(
          filter((value): value is string => value !== null),
          map((value: string) => value.trim().toLowerCase()),
          debounceTime(500),
          filter((value: string) => value.length >= MIN_QUERY_LENGTH || !value.length),
        )
        .subscribe((query) => {
          // Reset friends list before filtering
          this.contactsList.set([]);
          this.allContactsLoaded = false;

          this.loadMoreContacts(query);
        }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tabUpdated'].currentValue === 1) {
      this.getInitialContacts();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getInitialContacts(query = '') {
    this.subscriptions.add(
      this.friendsService.getContacts(query, 50).subscribe((contacts: IContactResponse) => {
        if (contacts.data) {
          this.contactsTotal.set(contacts.total);
          this.contactsList.set(contacts.data);
        }
      }),
    );
  }

  onContactsScroll() {
    const container = this.scrollContactsContainer.nativeElement;
    const bottomReached = container.scrollTop + container.clientHeight >= container.scrollHeight - 200;

    if (bottomReached && !this.allContactsLoaded && !this.isContactsFetching) {
      this.loadMoreContacts(this.searchContact.value || '');
    }
  }
  onEvent(event: {type: ContactEvent, contact: IContact}) {
    const list = this.contactsList();

    if (event.type === ContactEvent.Update) {
      this.friendsService.updateUserContact(event.contact).subscribe(() => {
        const updatedContacts = this.contactsList().map((contact) =>
          contact.id === event.contact.id ? event.contact : contact,
        );

        this.contactsList.set(updatedContacts);
      })
    }

    if (event.type === ContactEvent.Removed) {
      this.friendsService.removeUserContact(event.contact.id).subscribe(() => {
        this.contactsList.set(list.filter((f) => f.id !== event.contact.id));
        this.contactsTotal.set(this.contactsTotal() - 1);
        this.friendsService.setFriendsTotal(this.contactsTotal())
      })
    }

    if (event.type === ContactEvent.Invite) {
      this.friendsService.sendUserInvite(event.contact.id).subscribe({
        next: () => {
          this.dialogService.open(InfoDialogComponent, {
            data: {
              secondTitle: 'Invite sent!',
              text: 'You can send an invite to the same person once every 30 days.',
              buttons: [
                {
                  text: 'OK',
                  action: 'first' as ButtonAction,
                },
              ],
            }
          })
        },
        error: (errorResponse) => {
          if (errorResponse.status === 403) {
            this.notification.error(errorResponse.error.message);
          }
        }
      })
    }
  }

  loadMoreContacts(query: string) {
    if (this.allContactsLoaded || this.isContactsFetching) return;

    this.isContactsFetching = true;

    this.friendsService
      .getContacts(query, 50, this.contactsList().length)
      .pipe(
        exhaustMap((friendsResponse) => {
          if (friendsResponse.data.length) {
            this.contactsList.update((list) => [...list, ...friendsResponse.data]);
            this.allContactsLoaded = friendsResponse.total <= this.contactsList().length;
            this.contactsTotal.set(friendsResponse.total);
            this.friendsService.setContactsTotal(friendsResponse.total);
          }
          return of(null);
        }),
        finalize(() => (this.isContactsFetching = false)),
      )
      .subscribe();
  }
}
